import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

import { T, FONT_STACK } from '../containers/orders/orders.theme';
import { COPY } from '../containers/orders/orders.copy';
import { StatusFilter } from '../containers/orders/orders.selectors';
import useOrders from '../containers/orders/useOrders';
import {
  actionErrorCleared,
  cancelDialogClosed,
  cancelDialogOpened,
  filtersCleared,
  orderSelected,
  rangeChanged,
  searchChanged,
  staffFilterChanged,
  statusFilterChanged,
} from '../containers/orders/ordersScreenSlice';

import OrdersSummaryBand, { DateStrip } from '../containers/orders/OrdersSummaryBand';
import DateRangePopover from '../containers/orders/DateRangePopover';
import OrdersFilterBar from '../containers/orders/OrdersFilterBar';
import OrdersList from '../containers/orders/OrdersList';
import OrderDetailPanel from '../containers/orders/OrderDetailPanel';
import CancelOrderDialog from '../containers/orders/CancelOrderDialog';
import BillToCustomerDialog from '../containers/orders/BillToCustomerDialog';
import PayAccountDialog, { payAccountsOf } from '../containers/orders/PayAccountDialog';
import { fetchCustomers } from '../containers/customer/customersApi';
import {
  setCustomerDataFetched,
  setCustomers,
} from '../containers/customer/customerSlice';
import {
  OrdersEmpty,
  OrdersError,
  OrdersSkeleton,
} from '../containers/orders/OrdersPlaceholders';
import exportOrdersPdf from '../containers/orders/exportOrdersPdf';

import PrinterOrder from '../containers/orders/PrintOrder';
import { fetchMenu } from '../containers/menu/menuApi';
import { fetchPrinters } from '../containers/printing/printersApi';
import { setPrinters, setPrintersDataFetched } from '../containers/printing/printerSlice';
import { setCategories, setCategoryDataFetched } from '../containers/orders/categorySlice';
import { setProductDataFetched, setProducts } from '../containers/orders/productSlice';
import { buildPlan } from '../containers/printing/printPlan';
import { describeFailures, printStationTickets } from '../containers/printing/printJobs';
import { clearCart, loadOrderToCart } from '../containers/orders/orderSlice';

const FMT = 'YYYY-MM-DD';
const MANAGER_ROLES = ['manager', 'superadmin-superadmin'];

/**
 * A row action that failed. It reports itself here rather than replacing the
 * whole list with an error page — the other orders are still good.
 */
function ActionErrorStrip({ message, onDismiss }) {
  return (
    <div
      role="alert"
      style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '9px 12px',
        borderRadius: T.radiusControl,
        background: T.dangerTint,
        border: `1px solid ${T.dangerBorder}`,
      }}
    >
      <span style={{ flex: 1, fontSize: 12.5, color: T.danger }}>{message}</span>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        style={{
          border: 'none',
          background: 'transparent',
          color: T.danger,
          fontSize: 16,
          lineHeight: 1,
          cursor: 'pointer',
          padding: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}

/**
 * Orders.
 *
 * A row can be scanned and settled; editing and cancelling happen in the
 * detail panel only. Only the date range costs a request — status, waiter and
 * search all narrow the loaded set in memory.
 */
export default function OrderList() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const activeUser = useSelector((s) => s.login.activeUser);
  const token = useSelector((s) => s.login.token);
  const business = activeUser?.business;
  const currency = business?.currencyMark || '$';

  // Reprinting a kitchen ticket walks item -> category -> printer, so this
  // screen needs all three even though it never shows them.
  const { products, isProductsDataFetched } = useSelector((s) => s.products);
  const { categories, isCategoryDataFetched } = useSelector((s) => s.categories);
  const { printers, isPrintersDataFetched } = useSelector((s) => s.printers);

  const role = String(activeUser?.role || '').toLowerCase();
  const isWaiter = role === 'waiter';
  const canCancel = MANAGER_ROLES.includes(role);

  const {
    statusFilter,
    staffId,
    search,
    selectedOrderId,
    busyOrderId,
    actionError,
    cancelingOrderId,
    isCanceling,
    cancelError,
  } = useSelector((s) => s.ordersScreen);

  const {
    view,
    selectedEntry,
    status,
    error,
    range,
    reload,
    setOrderStatus,
    cancelOrder,
    billToCustomer,
  } = useOrders();

  const { customers, isCustomerDataFetched } = useSelector((s) => s.customers);

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [printTarget, setPrintTarget] = useState(null);
  const [billTarget, setBillTarget] = useState(null);
  const [customersLoading, setCustomersLoading] = useState(false);

  const dateAnchorRef = useRef(null);

  /**
   * Paying asks which account first.
   *
   * `payTarget` is the order waiting on that answer. Putting one BACK to
   * pending asks nothing — there is no account to name for money that is no
   * longer anywhere — so only a pending order opens the dialog.
   */
  const [payTarget, setPayTarget] = useState(null);

  /** The accounts row under the band. Open by default; folded away per session. */
  const [accountsOpen, setAccountsOpen] = useState(true);
  const searchRef = useRef(null);
  const listRef = useRef(null);

  // A waiter's range is locked to today — they never pick a date.
  useEffect(() => {
    if (!isWaiter) return;
    const today = moment().format(FMT);
    if (range.start !== today || range.end !== today) {
      dispatch(rangeChanged({ start: today, end: today }));
    }
  }, [isWaiter, range.start, range.end, dispatch]);

  // ── Actions ──────────────────────────────────────────────────────────────

  const handleSelect = useCallback(
    (id) => dispatch(orderSelected(id === selectedOrderId ? null : id)),
    [dispatch, selectedOrderId]
  );

  const handleEdit = useCallback(
    (entry) => {
      dispatch(clearCart());
      dispatch(loadOrderToCart(entry.order));
      navigate('/orders');
    },
    [dispatch, navigate]
  );

  /**
   * Opening the bill dialog also fetches the customer book, once.
   *
   * Not on entry to this screen: most visits here never bill anybody, and the
   * list can be long. The Customers tab fills the same slice, so whichever is
   * opened first pays for it.
   */
  const handleBillToCustomer = useCallback(
    (entry) => {
      setBillTarget(entry);

      if (isCustomerDataFetched || !business?._id) return;

      setCustomersLoading(true);
      fetchCustomers({ businessId: business._id, token })
        .then((rows) => {
          dispatch(setCustomers(rows));
          dispatch(setCustomerDataFetched(true));
        })
        .catch(() => {})
        .finally(() => setCustomersLoading(false));
    },
    [dispatch, business, token, isCustomerDataFetched]
  );

  /**
   * The customer's balance has moved, so the book on screen is stale. Marking
   * it unfetched is cheaper than guessing the new figure, and the Customers
   * tab pulls a correct one next time it is opened.
   */
  const confirmBill = useCallback(
    async (entry, customerId) => {
      await billToCustomer(entry, customerId);
      dispatch(setCustomerDataFetched(false));
    },
    [billToCustomer, dispatch]
  );

  /**
   * The Pay button, everywhere it appears.
   *
   * A pending order opens the account dialog; anything else is a Restore and
   * goes straight through. One handler so the row and the detail panel can
   * never behave differently.
   */
  const handleToggleStatus = useCallback(
    (entry) => {
      if (entry && entry.isPending) {
        setPayTarget(entry);
        return;
      }
      setOrderStatus(entry);
    },
    [setOrderStatus]
  );

  const confirmPay = useCallback(
    async (account) => {
      const entry = payTarget;
      setPayTarget(null);
      await setOrderStatus(entry, account);
    },
    [payTarget, setOrderStatus]
  );

  /** Cash, then the business's own wallets. Steady unless the business changes. */
  const payAccounts = useMemo(() => payAccountsOf(business), [business]);

  const handlePrint = useCallback((entry) => {
    setPrintTarget({
      orderNumber: entry.orderNumber,
      discount: entry.discount,
      date: entry.time.toISOString(),
      vatRate: entry.vatRate,
      vatAmount: entry.vatAmount,
      totalWithVat: entry.totalWithVat,
      // The name off the saved order, not whoever is at the till now — a
      // reprint has to name the person who actually served the table.
      waiterName: (entry.order && entry.order.user && entry.order.user.name) || '',
      products: (entry.order.items || []).map((item) => ({
        name: item.name,
        qty: item.quantity,
        salePrice: item.price,
      })),
    });
  }, []);

  /**
   * The menu and the printers, if this is the first screen opened.
   *
   * Both are normally already in the store from the order pad. Fetched here
   * only so the reprint button is not quietly dead for someone who came
   * straight to Orders.
   */
  useEffect(() => {
    if (!business?._id) return undefined;

    let cancelled = false;

    if (!isProductsDataFetched || !isCategoryDataFetched) {
      fetchMenu({ businessId: business._id, token })
        .then(({ menuItems, categories: rows }) => {
          if (cancelled) return;
          dispatch(setProducts(menuItems));
          dispatch(setProductDataFetched(true));
          dispatch(setCategories(rows));
          dispatch(setCategoryDataFetched(true));
        })
        .catch(() => {});
    }

    if (!isPrintersDataFetched) {
      fetchPrinters({ businessId: business._id, token })
        .then((rows) => {
          if (cancelled) return;
          dispatch(setPrinters(rows));
          dispatch(setPrintersDataFetched(true));
        })
        .catch(() => {});
    }

    return () => {
      cancelled = true;
    };
  }, [
    business,
    token,
    isProductsDataFetched,
    isCategoryDataFetched,
    isPrintersDataFetched,
    dispatch,
  ]);

  /**
   * Send the kitchen its ticket again.
   *
   * The plan is worked out here rather than asked for: the server only builds
   * one when an order is created, and this order was created some time ago.
   * The menu, the categories and the printers are all in the store already,
   * which is the same chain the server walks.
   */
  const handleReprintTickets = useCallback(
    async (entry) => {
      const order = entry.order || {};
      const plan = buildPlan(order.items || [], products, categories, printers);

      if (!plan.length) {
        alert(COPY.noStations);
        return;
      }

      const { failed } = await printStationTickets({
        order: {
          orderNumber: entry.orderNumber,
          tableNumber: order.tableNumber || '',
          orderType: order.orderType || '',
          note: order.note || '',
          // The raw stored value, for the same reason the ticket uses
          // payload.date on a new order.
          date: order.date,
          isReprint: true,
        },
        plan,
        waiterName: (order.user && order.user.name) || '',
      });

      // Silence is the success case — the paper coming out says it better
      // than a dialog would.
      if (failed.length) alert(describeFailures(failed));
    },
    [products, categories, printers]
  );

  const handleWiden = useCallback(() => {
    dispatch(
      rangeChanged({
        start: moment().subtract(6, 'days').format(FMT),
        end: moment().format(FMT),
      })
    );
  }, [dispatch]);

  const filterDescription = useMemo(() => {
    const parts = [];
    if (statusFilter !== StatusFilter.ALL) parts.push(`Status: ${statusFilter}`);
    if (view.activeStaffId) {
      const staff = view.staff.find((s) => s.id === view.activeStaffId);
      if (staff) parts.push(`Waiter: ${staff.name}`);
    }
    if (search.trim()) parts.push(`Search: "${search.trim()}"`);
    return parts.join('  ·  ');
  }, [statusFilter, view.activeStaffId, view.staff, search]);

  const handleExport = useCallback(() => {
    if (!view.visible.length) return;
    exportOrdersPdf({
      entries: view.visible,
      business,
      range,
      filters: filterDescription,
    });
  }, [view.visible, business, range, filterDescription]);

  // ── Keyboard ─────────────────────────────────────────────────────────────

  const flatEntries = useMemo(
    () => view.groups.reduce((all, group) => all.concat(group.entries), []),
    [view.groups]
  );

  useEffect(() => {
    const onKeyDown = (event) => {
      const target = event.target;
      const typing =
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable);

      // Escape always closes the panel, even from the search field.
      if (event.key === 'Escape') {
        if (typing && target !== searchRef.current) return;
        if (selectedOrderId) dispatch(orderSelected(null));
        if (target === searchRef.current) target.blur();
        return;
      }

      if (typing) return;

      if (event.key === '/') {
        event.preventDefault();
        searchRef.current?.focus();
        return;
      }

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'p') {
        if (!selectedEntry) return;
        event.preventDefault();
        handlePrint(selectedEntry);
        return;
      }

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        if (!flatEntries.length) return;
        event.preventDefault();

        const current = flatEntries.findIndex((e) => e.id === selectedOrderId);
        const step = event.key === 'ArrowDown' ? 1 : -1;
        const next =
          current === -1
            ? event.key === 'ArrowDown'
              ? 0
              : flatEntries.length - 1
            : Math.min(Math.max(current + step, 0), flatEntries.length - 1);

        dispatch(orderSelected(flatEntries[next].id));
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [dispatch, flatEntries, selectedOrderId, selectedEntry, handlePrint]);

  // ── Body ─────────────────────────────────────────────────────────────────

  const emptyReason = useMemo(() => {
    if (view.hasNarrowingFilter) {
      return filterDescription
        ? `Nothing matches ${filterDescription.toLowerCase()} in this date range.`
        : 'Nothing matches the current filters.';
    }
    return 'No orders were placed in this date range.';
  }, [view.hasNarrowingFilter, filterDescription]);

  const renderBody = () => {
    if (status === 'loading' && !view.visible.length) {
      return <OrdersSkeleton showSummary={!isWaiter} />;
    }

    if (status === 'error') {
      return (
        <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
          <OrdersError message={error} onRetry={reload} />
        </div>
      );
    }

    return (
      <>
        {isWaiter ? (
          <DateStrip range={range} orderCount={view.summaryCount} />
        ) : (
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <OrdersSummaryBand
              summary={view.summary}
              orderCount={view.summaryCount}
              range={range}
              currency={currency}
              dateAnchorRef={dateAnchorRef}
              onOpenDatePicker={() => setDatePickerOpen((v) => !v)}
              onExport={handleExport}
              accountsOpen={accountsOpen}
              onToggleAccounts={() => setAccountsOpen((open) => !open)}
            />
            {datePickerOpen && (
              <DateRangePopover
                range={range}
                anchorRef={dateAnchorRef}
                onClose={() => setDatePickerOpen(false)}
                onApply={(next) => {
                  dispatch(rangeChanged(next));
                  setDatePickerOpen(false);
                }}
              />
            )}
          </div>
        )}

        {actionError && (
          <ActionErrorStrip
            message={actionError}
            onDismiss={() => dispatch(actionErrorCleared())}
          />
        )}

        <OrdersFilterBar
          statusFilter={statusFilter}
          onStatusChange={(value) => dispatch(statusFilterChanged(value))}
          staff={view.staff}
          staffId={view.activeStaffId}
          onStaffChange={(value) => dispatch(staffFilterChanged(value))}
          search={search}
          onSearchChange={(value) => dispatch(searchChanged(value))}
          searchRef={searchRef}
          showStaffFilter={!isWaiter}
        />

        <section style={{ flex: 1, minHeight: 0, display: 'flex', gap: 16 }}>
          {view.isEmpty ? (
            <OrdersEmpty
              reason={emptyReason}
              canClearFilters={view.hasNarrowingFilter}
              onClearFilters={() => dispatch(filtersCleared())}
              onWiden={handleWiden}
            />
          ) : (
            <OrdersList
              listRef={listRef}
              groups={view.groups}
              selectedOrderId={selectedOrderId}
              busyOrderId={busyOrderId}
              currency={currency}
              showStaff={!isWaiter}
              canToggle
              onSelect={handleSelect}
              onToggleStatus={handleToggleStatus}
            />
          )}

          <OrderDetailPanel
            entry={selectedEntry}
            currency={currency}
            busy={selectedEntry ? selectedEntry.id === busyOrderId : false}
            canCancel={canCancel}
            canToggle
            onClose={() => dispatch(orderSelected(null))}
            onPrint={handlePrint}
            onReprintTickets={handleReprintTickets}
            onEdit={handleEdit}
            onCancel={(entry) => dispatch(cancelDialogOpened(entry.id))}
            onToggleStatus={handleToggleStatus}
            onBillToCustomer={handleBillToCustomer}
          />
        </section>
      </>
    );
  };

  const cancelTarget = cancelingOrderId ? view.byId.get(cancelingOrderId) || null : null;

  return (
    <main
      style={{
        height: 'calc(100vh - 70px)',
        boxSizing: 'border-box',
        padding: `18px ${T.screenPadding}px`,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        background: T.canvas,
        color: T.ink,
        fontFamily: FONT_STACK,
      }}
    >
      {renderBody()}

      {isWaiter && (
        <p
          style={{
            flexShrink: 0,
            margin: 0,
            fontSize: 12,
            color: T.inkFaint,
          }}
        >
          {COPY.waiterNotice}
        </p>
      )}

      {cancelTarget && (
        <CancelOrderDialog
          entry={cancelTarget}
          currency={currency}
          isCanceling={isCanceling}
          error={cancelError}
          onConfirm={cancelOrder}
          onClose={() => dispatch(cancelDialogClosed())}
        />
      )}

      {payTarget && (
        <PayAccountDialog
          entry={payTarget}
          accounts={payAccounts}
          currency={currency}
          busy={payTarget.id === busyOrderId}
          onClose={() => setPayTarget(null)}
          onConfirm={confirmPay}
        />
      )}

      {billTarget && (
        <BillToCustomerDialog
          entry={billTarget}
          customers={customers}
          loading={customersLoading}
          currency={currency}
          onClose={() => setBillTarget(null)}
          onConfirm={confirmBill}
        />
      )}

      {printTarget && (
        <PrinterOrder
          open
          onClose={() => setPrintTarget(null)}
          business={business}
          user={activeUser}
          data={printTarget}
        />
      )}
    </main>
  );
}
